/**
 * Global keyboard shortcuts (design Main.dc.html:173-178): ⌘K / Ctrl+K (also the Cyrillic «л» key)
 * opens the palette from anywhere; N (or «т») opens quick capture outside text fields when no
 * dialog, menu or the palette is open.
 */
import { useEffect, useRef } from 'react';
import { isCommandKey, isEditableTarget } from '../../lib/keyboard';
import { isBlockingLayerOpen } from '../../lib/layers';

export {
  PAGE_FILTER_KEY,
  PRIMARY_BULK_ACTION_EVENT,
  dispatchPrimaryBulkAction,
  isCommandKey,
  isEditableTarget,
  usePageFilterShortcut,
  usePrimaryBulkAction,
} from '../../lib/keyboard';

export interface GlobalShortcutHandlers {
  readonly onPalette: () => void;
  readonly onCapture: () => void;
}

const PALETTE_KEYS = new Set(['k', 'K', 'л', 'Л']);
const CAPTURE_KEYS = new Set(['n', 'N', 'т', 'Т']);

export function useGlobalShortcuts(handlers: GlobalShortcutHandlers): void {
  const latest = useRef(handlers);
  useEffect(() => {
    latest.current = handlers;
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isCommandKey(event) && !event.altKey && PALETTE_KEYS.has(event.key)) {
        event.preventDefault();
        latest.current.onPalette();
        return;
      }
      const plain = !event.metaKey && !event.ctrlKey && !event.altKey;
      if (plain && CAPTURE_KEYS.has(event.key) && !isEditableTarget(event.target) && !isBlockingLayerOpen()) {
        event.preventDefault();
        latest.current.onCapture();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
